import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const formatCurrency = (amount: number, currency: string = 'GHS') => new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

interface CifPaidTransaction {
    transaction_type: string;
    allocated_amount?: number | null;
    transactions?: { amount?: number | null } | null;
}

/**
 * Sum of the cif_30 + cif_70 amounts paid for this vehicle — the "paid toward CIF" figure, shared by
 * every CIF display. A CIF payment split across a container's vehicles counts only this vehicle's
 * share (`allocated_amount`); an unsplit one counts the whole transaction amount — the same rule as
 * `VehicleValueDisplay` below and the backend's `COALESCE(vt.allocated_amount, t.amount)`.
 */
export function sumCifPaid(vehicleTransactions: CifPaidTransaction[] | null | undefined): number {
    const vts = vehicleTransactions || [];
    return vts
        .filter(vt => vt.transaction_type === 'cif_30' || vt.transaction_type === 'cif_70')
        .reduce((sum, vt) => sum + ((vt.allocated_amount ?? vt.transactions?.amount) || 0), 0);
}

/** The "$cif / Bal: $outstanding" figures — shared presentation between VehicleCifDisplay and any aggregate (e.g. a container tile) that already has its own cif/outstanding numbers. */
export function CifBalanceFigures({ cif, outstanding, align = 'left' }: { cif: number; outstanding: number; align?: 'left' | 'right' }) {
    return (
        <div className={cn('text-sm leading-tight', align === 'right' && 'text-right')}>
            <p className="font-medium">${new Intl.NumberFormat('en-US').format(cif)}</p>
            <p className={cn('text-xs', outstanding > 1 ? 'text-red-500' : 'text-emerald-600')}>
                Bal: ${new Intl.NumberFormat('en-US').format(Math.abs(outstanding))}
            </p>
        </div>
    );
}

export function VehicleCifDisplay({ cif, vehicleTransactions }: { cif: number | null | undefined; vehicleTransactions: any[] | null | undefined }) {
    if (!cif) return <span className="text-muted-foreground">—</span>;
    const outstanding = cif - sumCifPaid(vehicleTransactions);
    return <CifBalanceFigures cif={cif} outstanding={outstanding} />;
}

/** One priced line in the `VehicleValueDisplay` breakdown — a single vehicle_transactions row. */
export interface VehicleValueItem {
    type: string;
    label: string;
    amount: number;
    fee: number;
    currency: string;
}

/** `VehicleValueItem`s sharing a currency, plus their combined amount+fee subtotal in that currency. */
export interface CurrencyGroup {
    currency: string;
    items: VehicleValueItem[];
    subtotal: number;
}

/**
 * Groups value items by currency, preserving each currency's first-appearance order (stable,
 * independent of object key iteration). Fixes the latent bug where a vehicle with e.g. a USD CIF
 * transaction and a GHS tax bill had the two amounts summed together into one meaningless number —
 * every caller must render per-group, never sum `subtotal` across groups.
 */
export function groupItemsByCurrency(items: VehicleValueItem[]): CurrencyGroup[] {
    const order: string[] = [];
    const byCurrency = new Map<string, VehicleValueItem[]>();
    for (const item of items) {
        if (!byCurrency.has(item.currency)) {
            byCurrency.set(item.currency, []);
            order.push(item.currency);
        }
        byCurrency.get(item.currency)!.push(item);
    }
    return order.map(currency => {
        const groupItems = byCurrency.get(currency)!;
        const subtotal = groupItems.reduce((s, i) => s + i.amount + i.fee, 0);
        return { currency, items: groupItems, subtotal };
    });
}

// Compact prefix used only for the collapsed multi-currency trigger (e.g. "US$1,234.00 +
// GH₵5,678.00") so the two figures are visually distinguishable at a glance. Currencies outside
// this map fall back to the full `formatCurrency` rendering (e.g. "EUR 1,234.00").
const COMPACT_CURRENCY_SYMBOLS: Record<string, string> = { USD: 'US$', GHS: 'GH₵' };

const formatCompactCurrency = (amount: number, currency: string): string => {
    const symbol = COMPACT_CURRENCY_SYMBOLS[currency];
    if (!symbol) return formatCurrency(amount, currency);
    return `${symbol}${new Intl.NumberFormat('en-US').format(amount)}`;
};

function ValueBreakdownBlock({ items, subtotal, showHeader }: { items: VehicleValueItem[]; subtotal: number; showHeader: boolean }) {
    const currency = items[0]?.currency;
    return (
        <div>
            {showHeader && (
                <div className="px-3 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase text-muted-foreground/70">{currency}</div>
            )}
            {items.map((item, idx) => (
                <div key={idx}>
                    <div className="flex justify-between gap-6 px-3 py-1.5 border-b border-border/50">
                        <span className="text-muted-foreground">{item.label}</span>
                        <span className="font-mono font-medium">{formatCurrency(item.amount, item.currency)}</span>
                    </div>
                    {item.fee > 0 && (
                        <div className="flex justify-between gap-6 px-3 py-1.5 border-b border-border/50">
                            <span className="text-muted-foreground">{item.label} Fee</span>
                            <span className="font-mono font-medium">{formatCurrency(item.fee, item.currency)}</span>
                        </div>
                    )}
                </div>
            ))}
            {(showHeader || items.length > 1 || items.some(i => i.fee > 0)) && (
                <div className="flex justify-between gap-6 px-3 py-1.5 bg-muted/50 font-semibold">
                    <span>Total</span>
                    <span className="font-mono">{formatCurrency(subtotal, currency)}</span>
                </div>
            )}
        </div>
    );
}

export function VehicleValueDisplay({ vehicleTransactions }: { vehicleTransactions: any[] | null | undefined }) {
    const vts = vehicleTransactions;
    if (!Array.isArray(vts) || vts.length === 0) return '-';

    const TYPE_LABELS: Record<string, string> = {
        cif: 'CIF', cif_30: 'CIF 30%', cif_70: 'CIF 70%',
        tax_bill: 'Tax Bill', port_charges: 'Port Charges',
        landed_cost: 'Landed Cost', steering_changed: 'Steering Conversion',
        levy: 'CHA Membership Levy',
    };

    const items = vts
        .filter((vt: any) => vt.transactions?.amount)
        .map((vt: any) => ({
            type: vt.transaction_type as string,
            label: TYPE_LABELS[vt.transaction_type] || vt.transaction_type,
            // A fan-out transaction (e.g. one GRA tax-bill payment split across a container's
            // vehicles) carries each vehicle's exact share on the vehicle_transactions row itself;
            // COALESCE to the full transaction amount for the (still-common) unsplit case.
            amount: (vt.allocated_amount ?? vt.transactions.amount) as number,
            fee: (vt.allocated_fee ?? vt.transactions.fee ?? 0) as number,
            currency: vt.transactions.currency as string || 'USD',
        }));

    if (items.length === 0) return '-';

    // Determine which items count toward the total
    const hasLanded = items.some((i: any) => i.type === 'landed_cost');
    const totalTypes = hasLanded
        ? ['landed_cost']
        : ['cif_30', 'cif_70', 'tax_bill', 'port_charges', 'steering_changed'];
    const totalItems = items.filter((i: any) => totalTypes.includes(i.type));
    const total = totalItems.reduce((s: number, i: any) => s + i.amount + i.fee, 0);

    if (total <= 0) return '-';

    const groups = groupItemsByCurrency(totalItems);
    const isMultiCurrency = groups.length > 1;

    const triggerLabel = isMultiCurrency
        ? groups.map(g => formatCompactCurrency(g.subtotal, g.currency)).join(' + ')
        : formatCurrency(total, totalItems[0]?.currency || 'USD');

    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="cursor-default border-b border-dashed border-muted-foreground/30">
                        {triggerLabel}
                    </span>
                </TooltipTrigger>
                <TooltipContent side="left" className="p-0">
                    <div className="min-w-[180px] text-xs">
                        {groups.map((group, idx) => (
                            <ValueBreakdownBlock key={idx} items={group.items} subtotal={group.subtotal} showHeader={isMultiCurrency} />
                        ))}
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
